import DateFormat from '../dateFormat'
import { LanguageCodes } from './languages'

type DateFormatOptions = {
  custom: {
    useMeridian: boolean
  }
  longDateFormat: Record<DateFormat, string>
}

// it needs to match the locale language code from moment library
export const MomentLanguageMap = {
  [LanguageCodes['en-US']]: 'en',
  [LanguageCodes['ca-ES']]: 'es',
  [LanguageCodes['es-ES']]: 'es',
  [LanguageCodes['pt-PT']]: 'pt',
  [LanguageCodes['pt-BR']]: 'pt-br',
}

const enUSDateFormatOptions: DateFormatOptions = {
  custom: {
    useMeridian: true,
  },
  longDateFormat: {
    [DateFormat.DATE_WITHOUT_YEAR]: 'MM/DD',
    [DateFormat.SHORT_DATE_WITHOUT_YEAR]: 'M/D',
    [DateFormat.DAY_OF_WEEK_ABBREV_COMMA_MONTH_DAY]: 'ddd, MMM DD',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_AT_FULL_TIME_WITH_MERIDIAN]:
      'dddd MMMM D, [at] hh:mma',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR_FULL_TIME_WITH_MERIDIAN_AND_SPACE]:
      'dddd MMMM D, YYYY, h:mm a',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR]: 'dddd MMMM D, YYYY',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY]: 'dddd MMMM D',
    [DateFormat.FULL_DATE_COMMA_PIPE_TIME]: 'MMMM DD, YYYY | hh:mma',
    [DateFormat.FULL_DATE_COMMA]: 'MMMM DD, YYYY',
    [DateFormat.FULL_DATE_COMMA_SHORT]: 'MMM D, YYYY',
    [DateFormat.FULL_DATE_TIME_AT]: 'MM/DD/YYYY [at] hh:mma',
    [DateFormat.FULL_DATE_TIME_SHORT_WITH_PIPE]: 'MM/DD/YYYY | h:mma',
    [DateFormat.FULL_DATE_TIME_WITH_PIPE]: 'MM/DD/YYYY | hh:mma',
    [DateFormat.FULL_DATE_TIME]: 'MM/DD/YYYY hh:mma',
    [DateFormat.FULL_DATE_YEAR_ABBREV]: 'MM/DD/YY',
    [DateFormat.SHORT_DATE_YEAR_ABBREV]: 'M/D/YY',
    [DateFormat.FULL_TIME_WITH_MERIDIAN_AND_SPACE]: 'hh:mm a',
    [DateFormat.FULL_TIME_WITH_MERIDIAN]: 'hh:mma',
    [DateFormat.FULL_TIME_WITHOUT_MERIDIAN]: 'hh:mm',
    [DateFormat.MONTH_AND_DAY]: 'MMM D',
    [DateFormat.MONTH_AND_YEAR]: 'MMMM YYYY',
    [DateFormat.ONLY_FULL_DAY]: 'dddd',
    [DateFormat.ONLY_MERIDIAN]: 'a',
    [DateFormat.TIME_WITH_MERIDIAN_UPPER]: 'h:mmA',
    [DateFormat.TIME_WITH_MERIDIAN]: 'h:mma',
    [DateFormat['24_HOURS_FORMAT_WITH_SECONDS']]: 'HH:mm:ss',
    [DateFormat['24_HOURS_FORMAT']]: 'HH:mm',
  },
}

const esESDateFormatOptions: DateFormatOptions = {
  custom: {
    useMeridian: false,
  },
  longDateFormat: {
    [DateFormat.DATE_WITHOUT_YEAR]: 'DD/MM',
    [DateFormat.SHORT_DATE_WITHOUT_YEAR]: 'D/M',
    [DateFormat.DAY_OF_WEEK_ABBREV_COMMA_MONTH_DAY]: 'ddd, DD [de] MMM',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_AT_FULL_TIME_WITH_MERIDIAN]:
      'dddd, D [de] MMMM, [a las] HH:mm',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR_FULL_TIME_WITH_MERIDIAN_AND_SPACE]:
      'dddd D [de] MMMM, YYYY, HH:mm',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR]: 'dddd, D [de] MMMM, YYYY',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY]: 'dddd, D [de] MMMM',
    [DateFormat.FULL_DATE_COMMA_PIPE_TIME]: 'DD [de] MMMM, YYYY | HH:mm',
    [DateFormat.FULL_DATE_COMMA]: 'DD [de] MMMM, YYYY',
    [DateFormat.FULL_DATE_COMMA_SHORT]: 'D [de] MMM, YYYY',
    [DateFormat.FULL_DATE_TIME_AT]: 'DD/MM/YYYY [a las] HH:mm',
    [DateFormat.FULL_DATE_TIME_SHORT_WITH_PIPE]: 'DD/MM/YYYY | HH:mm',
    [DateFormat.FULL_DATE_TIME_WITH_PIPE]: 'DD/MM/YYYY | HH:mm',
    [DateFormat.FULL_DATE_TIME]: 'DD/MM/YYYY HH:mm',
    [DateFormat.FULL_DATE_YEAR_ABBREV]: 'DD/MM/YY',
    [DateFormat.SHORT_DATE_YEAR_ABBREV]: 'D/M/YY',
    [DateFormat.FULL_TIME_WITH_MERIDIAN_AND_SPACE]: 'HH:mm',
    [DateFormat.FULL_TIME_WITH_MERIDIAN]: 'HH:mm',
    [DateFormat.FULL_TIME_WITHOUT_MERIDIAN]: 'HH:mm',
    [DateFormat.MONTH_AND_DAY]: 'D MMM',
    [DateFormat.MONTH_AND_YEAR]: 'MMMM YYYY',
    [DateFormat.ONLY_FULL_DAY]: 'dddd',
    [DateFormat.ONLY_MERIDIAN]: 'a',
    [DateFormat.TIME_WITH_MERIDIAN_UPPER]: 'HH:mm',
    [DateFormat.TIME_WITH_MERIDIAN]: 'HH:mm',
    [DateFormat['24_HOURS_FORMAT_WITH_SECONDS']]: 'HH:mm:ss',
    [DateFormat['24_HOURS_FORMAT']]: 'HH:mm',
  },
}

const ptPTDateFormatOptions: DateFormatOptions = {
  custom: {
    useMeridian: false,
  },
  longDateFormat: {
    [DateFormat.DATE_WITHOUT_YEAR]: 'DD/MM',
    [DateFormat.SHORT_DATE_WITHOUT_YEAR]: 'D/M',
    [DateFormat.DAY_OF_WEEK_ABBREV_COMMA_MONTH_DAY]: 'ddd, DD [de] MMM',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_AT_FULL_TIME_WITH_MERIDIAN]:
      'dddd, D [de] MMMM, [às] HH[h]mm',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR_FULL_TIME_WITH_MERIDIAN_AND_SPACE]:
      'dddd D [de] MMMM, YYYY, HH[h]mm',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR]: 'dddd, D [de] MMMM, YYYY',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY]: 'dddd, D [de] MMMM',
    [DateFormat.FULL_DATE_COMMA_PIPE_TIME]: 'DD [de] MMMM, YYYY | HH[h]mm',
    [DateFormat.FULL_DATE_COMMA]: 'DD [de] MMMM, YYYY',
    [DateFormat.FULL_DATE_COMMA_SHORT]: 'D [de] MMM, YYYY',
    [DateFormat.FULL_DATE_TIME_AT]: 'DD/MM/YYYY [às] HH[h]mm',
    [DateFormat.FULL_DATE_TIME_SHORT_WITH_PIPE]: 'DD/MM/YYYY | HH[h]mm',
    [DateFormat.FULL_DATE_TIME_WITH_PIPE]: 'DD/MM/YYYY | HH[h]mm',
    [DateFormat.FULL_DATE_TIME]: 'DD/MM/YYYY HH[h]mm',
    [DateFormat.FULL_DATE_YEAR_ABBREV]: 'DD/MM/YY',
    [DateFormat.SHORT_DATE_YEAR_ABBREV]: 'D/M/YY',
    [DateFormat.FULL_TIME_WITH_MERIDIAN_AND_SPACE]: 'HH[h]mm',
    [DateFormat.FULL_TIME_WITH_MERIDIAN]: 'HH[h]mm',
    [DateFormat.FULL_TIME_WITHOUT_MERIDIAN]: 'HH[h]mm',
    [DateFormat.MONTH_AND_DAY]: 'D MMM',
    [DateFormat.MONTH_AND_YEAR]: 'MMMM YYYY',
    [DateFormat.ONLY_FULL_DAY]: 'dddd',
    [DateFormat.ONLY_MERIDIAN]: 'a',
    [DateFormat.TIME_WITH_MERIDIAN_UPPER]: 'HH[H]mm',
    [DateFormat.TIME_WITH_MERIDIAN]: 'HH[h]mm',
    [DateFormat['24_HOURS_FORMAT_WITH_SECONDS']]: 'HH:mm:ss',
    [DateFormat['24_HOURS_FORMAT']]: 'HH:mm',
  },
}

const ptBRDateFormatOptions: DateFormatOptions = {
  custom: {
    useMeridian: false,
  },
  longDateFormat: {
    [DateFormat.DATE_WITHOUT_YEAR]: 'DD/MM',
    [DateFormat.SHORT_DATE_WITHOUT_YEAR]: 'D/M',
    [DateFormat.DAY_OF_WEEK_ABBREV_COMMA_MONTH_DAY]: 'ddd, DD [de] MMM',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_AT_FULL_TIME_WITH_MERIDIAN]:
      'dddd, D [de] MMMM, [às] HH[h]mm',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR_FULL_TIME_WITH_MERIDIAN_AND_SPACE]:
      'dddd D [de] MMMM, YYYY, HH[h]mm',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY_COMMA_YEAR]: 'dddd, D [de] MMMM, YYYY',
    [DateFormat.DAY_OF_WEEK_MONTH_DAY]: 'dddd, D [de] MMMM',
    [DateFormat.FULL_DATE_COMMA_PIPE_TIME]: 'DD [de] MMMM, YYYY | HH[h]mm',
    [DateFormat.FULL_DATE_COMMA]: 'DD [de] MMMM, YYYY',
    [DateFormat.FULL_DATE_COMMA_SHORT]: 'D [de] MMM, YYYY',
    [DateFormat.FULL_DATE_TIME_AT]: 'DD/MM/YYYY [às] HH[h]mm',
    [DateFormat.FULL_DATE_TIME_SHORT_WITH_PIPE]: 'DD/MM/YYYY | HH[h]mm',
    [DateFormat.FULL_DATE_TIME_WITH_PIPE]: 'DD/MM/YYYY | HH[h]mm',
    [DateFormat.FULL_DATE_TIME]: 'DD/MM/YYYY HH[h]mm',
    [DateFormat.FULL_DATE_YEAR_ABBREV]: 'DD/MM/YY',
    [DateFormat.SHORT_DATE_YEAR_ABBREV]: 'D/M/YY',
    [DateFormat.FULL_TIME_WITH_MERIDIAN_AND_SPACE]: 'HH[h]mm',
    [DateFormat.FULL_TIME_WITH_MERIDIAN]: 'HH[h]mm',
    [DateFormat.FULL_TIME_WITHOUT_MERIDIAN]: 'HH:mm',
    [DateFormat.MONTH_AND_DAY]: 'D MMM',
    [DateFormat.MONTH_AND_YEAR]: 'MMMM YYYY',
    [DateFormat.ONLY_FULL_DAY]: 'dddd',
    [DateFormat.ONLY_MERIDIAN]: 'a',
    [DateFormat.TIME_WITH_MERIDIAN_UPPER]: 'HH[H]mm',
    [DateFormat.TIME_WITH_MERIDIAN]: 'HH[h]mm',
    [DateFormat['24_HOURS_FORMAT_WITH_SECONDS']]: 'HH:mm:ss',
    [DateFormat['24_HOURS_FORMAT']]: 'HH:mm',
  },
}

export const DateFormatOptionsMap = {
  [LanguageCodes['en-US']]: enUSDateFormatOptions,
  [LanguageCodes['ca-ES']]: esESDateFormatOptions,
  [LanguageCodes['es-ES']]: esESDateFormatOptions,
  [LanguageCodes['pt-PT']]: ptPTDateFormatOptions,
  [LanguageCodes['pt-BR']]: ptBRDateFormatOptions,
}
